import React, { FC, Suspense, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { EIconSize } from 'utils/constants/app/ui';
import {
  competitions,
  regions,
  sports
} from 'utils/constants/sportsbook/data-fields';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { isMobile } from 'utils/is-mobile';
import { addRegionNameToCompetition } from 'utils/sportsbook/add-region-name-to-competition';
import { getLivePreMatchSportTypes } from 'utils/sportsbook/sport-data';
import RidGenerator from 'utils/swarm/rid-generator';
import { ENotRealSports } from 'interfaces/not-real-sports';
import { Region as RegionType, Sport } from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import { OlympicSkeleton } from './Skeleton';
import { ConditionalWrapper } from 'components/ConditionalWrapper';
import { GlobalIcon } from 'components/GlobalIcon';
import { NavigationCollapse } from 'components/NavigationCollapse';
import {
  TopLeagueCompactLayout,
  TSportDataWithCompetitions
} from 'newcomponents/Shared/sportsbook/TopLeagueCompactLayout';
import { useSportsbookConfigs } from 'hooks/sportsbook/useSportsbookConfigs';
import {
  setNavigationWrapperHeightChanged,
  setSwarmResponse
} from 'store/actions/new-sportsbook';
import { getSwarmResponse } from 'store/selectors/new-sportsbook';

type TOlympicCompetitionCompactLayoutProps = {
  fromSportsList?: boolean;
  showCollapse?: boolean;
  sportIconSize?: number;
  regionIconSize?: number;
  externalDataSource?: Record<string, Sport>;
  addGameStartTsInRequest?: boolean;
  openFromSideBarPopup?: boolean;
};

export const OlympicCompetitionCompactLayout: FC<
  TOlympicCompetitionCompactLayoutProps
> = ({
  fromSportsList = false,
  showCollapse = !isMobile(),
  sportIconSize,
  regionIconSize,
  externalDataSource,
  addGameStartTsInRequest,
  openFromSideBarPopup
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const { fixedScroll } = useSportsbookConfigs();

  const internalDataSource =
    useSelector(getSwarmResponse)?.[ENotRealSports.olympicCompetition];

  const data = externalDataSource || internalDataSource;

  const sportData = useMemo(() => {
    if (data) {
      return addRegionNameToCompetition(
        { sport: { ...data } },
        false
      ) as TSportDataWithCompetitions;
    }
  }, [data]);

  const getData = (rid: string) => {
    const where = {
      competition: { info: { is_olympic: true } },
      game: {
        '@or': [
          {
            type: { '@in': [0, 2] }
          },
          {
            visible_in_prematch: 1,
            type: 1
          }
        ]
      },
      sport: {
        type: {
          '@in': getLivePreMatchSportTypes(undefined)
        }
      }
    };

    const relations: Record<string, string[]> = {
      ...sports,
      ...regions,
      competition: [...competitions.competition]
    };

    if (addGameStartTsInRequest) {
      relations.game = ['start_ts'];
    }

    executeCommand(
      rid,
      relations,
      where,
      (data: {
        sport: Record<string, Sport>;
        region: Record<string, RegionType>;
      }) => {
        dispatch(
          setSwarmResponse({ [ENotRealSports.olympicCompetition]: data.sport })
        );
      },
      true,
      (data: { sport: Record<string, Sport> }) => {
        dispatch(
          setSwarmResponse({ [ENotRealSports.olympicCompetition]: data.sport })
        );
      }
    );
  };

  useEffect(() => {
    if (!SpringConfigs.MOCKED_DATA) {
      const rId = RidGenerator.gForSubscribe('OLYMPIC_COMPETITION_COMPACT');

      getData(rId);

      return () => {
        unsubscribeCommand(rId);
      };
    }
  }, []);

  const onToggle = (active: boolean) => {
    if (!active && !isMobile() && !fixedScroll) {
      dispatch(setNavigationWrapperHeightChanged());
    }
  };

  if (!sportData) {
    if (fromSportsList) {
      return <OlympicSkeleton />;
    }

    return;
  }

  return (
    <ConditionalWrapper
      condition={showCollapse}
      wrapper={children => (
        <NavigationCollapse
          onChange={onToggle}
          key="olympicCompetition"
          title={t('sportsbook.olympicCompetition')}
          icon={
            <GlobalIcon
              lib="sports"
              name={`olympicCompetition${
                SpringConfigs.OLYMPIC_ICON_OTHER ? 'other' : ''
              }`}
              size={EIconSize._24}
              className="text-color"
            />
          }
          wrapperClassName="sportsList__sport olympicCompetition__wrapper"
          headerClassName="sportsList__sport__header olympicCompetition__header"
        >
          {children}
        </NavigationCollapse>
      )}
    >
      <Suspense>
        <TopLeagueCompactLayout
          sportData={sportData}
          fromSportsList={fromSportsList}
          sportIconSize={sportIconSize}
          regionIconSize={regionIconSize}
          openFromSideBarPopup={openFromSideBarPopup}
        />
      </Suspense>
    </ConditionalWrapper>
  );
};
